import { useCallback, useEffect, useRef, useState } from 'react'

export type ConnectionState = 'disconnected' | 'connecting' | 'connected'

export type ControlMessage = {
  type: string
  [key: string]: unknown
}

type UseVapiWebSocketOptions = {
  url: string | null
  onAudioData: (data: Int16Array) => void
  onControlMessage?: (message: ControlMessage) => void
  onClose?: () => void
  onError?: (error: Event) => void
}

export const useVapiWebSocket = ({
  url,
  onAudioData,
  onControlMessage,
  onClose,
  onError,
}: UseVapiWebSocketOptions) => {
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('disconnected')
  const socketRef = useRef<WebSocket | null>(null)

  const callbacksRef = useRef({
    onAudioData,
    onControlMessage,
    onClose,
    onError,
  })
  callbacksRef.current = {
    onAudioData,
    onControlMessage,
    onClose,
    onError,
  }

  useEffect(() => {
    if (!url) {
      setConnectionState('disconnected')
      return
    }

    setConnectionState('connecting')
    const socket = new WebSocket(url)
    socket.binaryType = 'arraybuffer'
    socketRef.current = socket

    socket.onopen = () => {
      setConnectionState('connected')
    }

    socket.onmessage = (event: MessageEvent) => {
      if (event.data instanceof ArrayBuffer) {
        const audioData = new Int16Array(event.data)
        callbacksRef.current.onAudioData(audioData)
      } else if (typeof event.data === 'string') {
        try {
          const message = JSON.parse(event.data) as ControlMessage
          callbacksRef.current.onControlMessage?.(message)
        } catch {
          // non-JSON text message, ignore
        }
      }
    }

    socket.onclose = () => {
      setConnectionState('disconnected')
      socketRef.current = null
      callbacksRef.current.onClose?.()
    }

    socket.onerror = (error) => {
      callbacksRef.current.onError?.(error)
    }

    return () => {
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close()
      }
      socketRef.current = null
    }
  }, [url])

  const send = useCallback((data: ArrayBuffer) => {
    const socket = socketRef.current
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(data)
    }
  }, [])

  const disconnect = useCallback(() => {
    const socket = socketRef.current
    if (socket) {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'end-call' }))
      }
      socket.close()
      socketRef.current = null
    }
  }, [])

  return {
    connectionState,
    send,
    disconnect,
  }
}
